import type en from "./en";
import type { Messages } from "./types";

export default {
  common: {
    loading: "読み込み中…",
    save: "保存",
    saving: "保存中…",
    cancel: "キャンセル",
    confirm: "確認",
    delete: "削除",
    close: "閉じる",
    copy: "コピー",
    dash: "—",
    genericError: "エラーが発生しました",
    confirmPasswordPlaceholder: "パスワードの確認",
    passwordMismatch: "パスワードが一致しません",
  },
  plan: {
    FREE: "無料",
    PAID: "有料",
  },
  nav: {
    console: "コンソール",
    home: "ホーム",
    apiKeys: "APIキー",
    subscription: "サブスクリプション",
    myAccount: "マイアカウント",
    logout: "ログアウト",
    openMenu: "メニューを開く",
  },
  auth: {
    tagline: "アカウント・APIキー管理",
    emailPlaceholder: "メールアドレス",
    passwordPlaceholder: "パスワード",
    passwordMinPlaceholder: "パスワード（8文字以上）",
    loginButton: "ログイン",
    loggingIn: "ログイン中…",
    noAccount: "アカウントをお持ちでないですか?",
    signUp: "登録する",
    createAccount: "アカウントを作成",
    creating: "作成中…",
    alreadyAccount: "既にアカウントをお持ちですか?",
    signIn: "ログイン",
    forgotPasswordLink: "パスワードをお忘れですか?",
  },
  passwordReset: {
    forgotTitle: "パスワードをお忘れの場合",
    forgotDescription:
      "メールアドレスを入力してください。パスワードリセット用のリンクをお送りします。",
    emailSentTitle: "メールを送信しました",
    emailSentDescription:
      "このメールアドレスのアカウントが存在する場合、数分以内にリセットリンクが届きます。",
    submit: "リセットリンクを送信",
    submitting: "送信中…",
    backToLogin: "ログインに戻る",
    resetTitle: "パスワードをリセット",
    resetDescription: "アカウントの新しいパスワードを設定してください。",
    newPasswordPlaceholder: "新しいパスワード（8文字以上）",
    resetSubmit: "パスワードをリセット",
    resetSubmitting: "リセット中…",
    resetSuccessTitle: "パスワードをリセットしました",
    resetSuccessDescription: "新しいパスワードでログインできます。",
    invalidTokenTitle: "無効なリンク",
    invalidTokenDescription: "このリセットリンクは無効か、有効期限が切れています。",
    requestNewLink: "新しいリンクをリクエスト",
  },
  account: {
    title: "マイアカウント",
    subtitle: "プロフィール、パスワード、アカウントを管理します",
    profile: {
      title: "プロフィール",
      emailLabel: "メールアドレス",
      emailDescription: "コンソールへのログインに使用されます",
      planLabel: "プラン",
      planDescription: "アカウントに関連付けられたプラン",
    },
    security: {
      title: "セキュリティ",
      currentPasswordPlaceholder: "現在のパスワード",
      newPasswordPlaceholder: "新しいパスワード（8文字以上）",
      submit: "パスワードを変更",
      updating: "更新中…",
    },
    danger: {
      title: "危険ゾーン",
      deleteLabel: "アカウントを削除",
      deleteDescription: "この操作は取り消せず、すべてのAPIキーが即座に無効になります",
      passwordPlaceholder: "パスワード",
      deleteButton: "削除",
      confirmTitle: "アカウントを完全に削除しますか?",
      confirmDescription: "すべてのAPIキーが無効化され、この操作は取り消せません。",
    },
    toast: {
      emailUpdated: "メールアドレスを更新しました",
      passwordUpdated: "パスワードを更新しました",
    },
  },
  apiKeys: {
    title: "APIキー",
    subtitle: "セルフホスト型インスタンスがライセンスを検証するために使用するキーを管理します。",
    newKey: "新しいキー",
    empty: "APIキーはまだありません。",
    table: {
      key: "キー",
      name: "名前",
      created: "作成日",
      lastUsed: "最終使用日",
      status: "ステータス",
    },
    unnamed: "名前なしのキー",
    revoked: "無効化済み",
    active: "有効",
    revokeAria: "無効化",
    confirmRevokeTitle: "このAPIキーを無効化しますか?",
    confirmRevokeDescription:
      "このキーを使用しているセルフホスト型インスタンスは、直ちにライセンス確認に失敗します。この操作は取り消せません。",
    dialog: {
      createdTitle: "APIキーを作成しました",
      createdDescription: "今すぐこのキーをコピーしてください。二度と表示されません。",
      copyAria: "コピー",
      createTitle: "APIキーを作成",
      createDescription:
        "このキーにより、セルフホスト型インスタンスがライセンスを検証できるようになります。",
      namePlaceholder: "名前（例: 本番サーバー）",
      generate: "キーを生成",
      generating: "作成中…",
    },
    toast: {
      revoked: "キーを無効化しました",
    },
  },
  billing: {
    subtitle: "プラン、サブスクリプション、請求書を管理します",
    yourPlan: "現在のプラン",
    planDescription: {
      FREE: "基本機能、標準的な制限",
      PAID: "すべての機能へのフルアクセス",
    },
    yourSubscription: "サブスクリプション",
    subscriptionLabel: "サブスクリプション",
    noActiveSubscription: "有効なサブスクリプションはありません",
    nextDueDate: "{{label}}・次回請求日 {{date}}",
    manage: "サブスクリプションを管理",
    redirecting: "リダイレクト中…",
    selfHostedPremium: "Self-hosted Premium",
    cloud: "Cloud",
    resync: "支払いを確認",
    resyncing: "確認中…",
    resyncFound: "支払いが見つかりました。サブスクリプションが有効になりました。",
    resyncNotFound: "支払いは見つかりませんでした。",
    status: {
      active: "有効",
      trialing: "試用期間",
      past_due: "支払い延滞",
      canceled: "解約済み",
      unpaid: "未払い",
      incomplete: "未完了",
      incomplete_expired: "期限切れ",
    },
    invoices: {
      title: "請求書",
      empty: "請求書はまだありません",
      download: "ダウンロード",
      status: {
        paid: "支払い済み",
        open: "保留中",
        void: "無効",
        uncollectible: "回収不能",
        draft: "下書き",
      },
    },
  },
  checkout: {
    cancel: {
      title: "支払いがキャンセルされました",
      description: "料金は請求されていません。いつでも再度お試しいただけます。",
      backToAccount: "アカウントに戻る",
    },
    success: {
      notFoundTitle: "セッションが見つかりません",
      notFoundDescription: "この支払いを確認できませんでした。",
      verifyingTitle: "支払いを確認しています…",
      verifyingDescription: "少々お待ちください。",
      confirmedTitle: "支払いが確認されました",
      activating: "サブスクリプションを有効化しています…",
      timeoutDescription:
        "有効化に予想より時間がかかっています。お支払いは確認済みです。まもなく有効化が完了する見込みです。このページを更新するか、サポートまでお問い合わせください。",
      refresh: "更新",
      activatedTitle: "サブスクリプションが有効化されました",
      activatedSelfHosted:
        "ありがとうございます！ セルフホスト型インスタンスを有効化するにはAPIキーを作成してください。",
      activatedCloud: "ありがとうございます！ 有料プランが有効になりました。",
      createApiKey: "APIキーを作成",
      backToAccount: "アカウントに戻る",
    },
  },
  home: {
    title: "ホーム",
    welcome: "ようこそ、{{email}}さん",
    planLabel: "プラン",
    activeApiKeys: "有効なAPIキー",
    manageApiKeys: "APIキーを管理",
    gettingStarted: {
      title: "はじめに",
      description:
        "APIキーを作成して、最初のセルフホスト型インスタンスをこのライセンスサーバーに接続しましょう。",
      action: "最初のAPIキーを作成",
    },
  },
  error: {
    boundary: "予期しないエラーが発生しました。",
    backHome: "ホームに戻る",
  },
  errors: {
    STRIPE_ERROR:
      "決済プロバイダーでエラーが発生しました。もう一度お試しいただくか、サポートにお問い合わせください。",
    NO_STRIPE_CUSTOMER:
      "管理するサブスクリプションがまだありません。先にアップグレードしてください。",
    CHECKOUT_SESSION_NOT_FOUND: "このチェックアウトセッションが見つかりません。",
    API_KEY_NOT_FOUND: "このAPIキーが見つかりません。",
    INVALID_RESET_TOKEN: "このパスワード再設定リンクは無効か、有効期限が切れています。",
    ACCOUNT_ALREADY_EXISTS: "このメールアドレスのアカウントは既に存在します。",
    INVALID_CREDENTIALS: "メールアドレスまたはパスワードが正しくありません。",
  },
  settings: {
    appearance: {
      sectionTitle: "外観",
    },
    language: {
      label: "表示言語",
      description: "コンソールで使用する言語を選択してください",
    },
    theme: {
      label: "テーマ",
      description: "コンソールの配色を選択してください",
      options: {
        light: "ライト",
        dark: "ダーク",
        system: "システム",
      },
    },
  },
} satisfies Messages<typeof en>;
